import { CIS_ACCOUNTS } from '../../constants/constants';
import IAccountDto from '../../domain/dto/IAccountDto';
import { localStorageContainer } from '../../domain/services';
import { IAccountType, ICreditData } from '../../domain/services/IConnectApi';

type ICreditDataAccount = ICreditData['accounts'][0];

export type IAccount = {
  accountIdentifiers: ICreditDataAccount['accountIdentifiers'];
  accountSubType?: IAccountType;
  connected: boolean;
  connectedProviderId?: ICreditDataAccount['connectedProviderId']
  balance?: number;
};

export type tuAccount = {
  logo?: ICreditDataAccount['attoProviderLogo'];
  attoProviderId?: ICreditDataAccount['attoProviderId'];
  attoBrandId?: ICreditDataAccount['attoBrandId'];
  accounts: IAccount[];
};

export type tuAccounts = {
  [k: string]: tuAccount;
};

export type ISummaryAccounts = IAccountDto & { bankNameFromUrl?: string };

const reduceFn = (acc: tuAccounts, curr: ICreditData['accounts'][0]) => {
  const account = {
    accountIdentifiers: curr.accountIdentifiers,
    accountSubType: curr.accountType,
    connected: curr.connected || false,
    connectedProviderId: curr.connectedProviderId
  };
  const bankName = curr.attoBrandName || curr.providerName;
  if (acc[bankName]) {
    acc[bankName].accounts.push(account);
  } else {
    acc[bankName] = {
      attoBrandId: curr.attoBrandId || null,
      attoProviderId: curr.attoProviderId || null,
      logo: curr.attoProviderLogo || null,
      accounts: [account]
    };
  }

  return acc;
};

export function filterTuAccounts(tuAccounts: ICreditData['accounts']) {
  const filteredAccounts = tuAccounts.filter(
    a => !a.isClosed && ['CurrentAccount', 'CreditCard', 'Savings'].includes(a.accountType)
  );

  const unconfirmedAccounts = filteredAccounts
    .filter(acc => !acc.connectionSupported)
    .reduce(reduceFn, {} as tuAccounts);
  const discoveredAccounts = filteredAccounts.filter(acc => acc.connectionSupported).reduce(reduceFn, {} as tuAccounts);

  return { discoveredAccounts, unconfirmedAccounts };
}

export function mergeByAccountRef<T extends IAccountDto>(a: T[], b: T[]): T[] {
  const seen = new Map<unknown, T>();
  for (const item of [...a, ...b]) {
    seen.set(item.accountNumber, item);
  }
  return [...seen.values()];
}

export const getMatchedConnectedAccounts = (
  discoveredAccounts: tuAccounts = {},
  connectionSummaryAccounts: ISummaryAccounts[] = []
) => {
  const storedAccounts = (localStorageContainer.getItemByUserId(CIS_ACCOUNTS) || []) as ISummaryAccounts[];
  const mergedAccounts = mergeByAccountRef(storedAccounts, connectionSummaryAccounts);

  const providerIdToKeyMap = new Map<number, string>();
  for (const dak in discoveredAccounts) {
    for (const da of discoveredAccounts[dak].accounts) {
      const providerId = da.connectedProviderId;
      if (providerId) providerIdToKeyMap.set(providerId, dak);
    }
  }

  for (const ma of mergedAccounts) {
    let discoveredAccountMatched = false;

    for (const dak in discoveredAccounts) {
      const target = ma.accountNumber?.slice(-4);
      const matchAccount = discoveredAccounts[dak].accounts.find(a => {
        if (a.connectedProviderId !== ma.providerId)
          return false;
        const idx = a.accountIdentifiers.findIndex(
          i => target != undefined && target !== '' && i.value.slice(-4) == target
        );
        return idx >= 0;
      });

      if (matchAccount) {
        matchAccount.connected = true;
        discoveredAccountMatched = true;
      }
    }

    if (discoveredAccountMatched) continue;
    
    const groupKey = (ma.bankNameFromUrl || '') in discoveredAccounts
        ? ma.bankNameFromUrl
        : (ma.displayName || '') in discoveredAccounts
          ? ma.displayName
          : providerIdToKeyMap.get(ma.providerId);

    if (groupKey) {
      discoveredAccounts[groupKey].accounts.push({
        accountIdentifiers: [{ type: 'account_number', value: ma.accountNumber || '' }],
        connected: true
      });
      continue;
    } else if (ma.bankNameFromUrl) {
      discoveredAccounts[ma.bankNameFromUrl] = {
        accounts: [
          { connected: true, accountIdentifiers: [{ type: 'account_number', value: ma.accountNumber || '' }] }
        ]
      };
    }
  }

  return discoveredAccounts;
};

export const getConnectedAccountsCount = (matchedDiscoveredAccounts: tuAccounts) => {
  let connectedAccountsCount = 0;
  let totalAccountsCount = 0;
  for (const key in matchedDiscoveredAccounts || {}) {
    totalAccountsCount += matchedDiscoveredAccounts[key]?.accounts?.length ?? 0;
    connectedAccountsCount += matchedDiscoveredAccounts[key]?.accounts?.filter(a => a.connected).length ?? 0;
  }
  return { totalAccountsCount, connectedAccountsCount };
};
